import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { useVisitTracker } from "@/lib/visitTracker";
import { startVisitorTracker } from "@/lib/visitorTracker";
import { CampaignProvider, AmericaPromoTopBar, FestiveConfetti } from "@/lib/america250";
import { apiClient } from "@/lib/api";
import { capture } from "@/lib/analytics";

import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import LessonPlayer from "@/pages/LessonPlayer";
import Leaderboard from "@/pages/Leaderboard";
import Library from "@/pages/Library";
import Pricing from "@/pages/Pricing";
import PowerBoosts from "@/pages/PowerBoosts";
import BillingSuccess from "@/pages/BillingSuccess";
import Settings from "@/pages/Settings";
import CoachPage from "@/pages/CoachPage";
import AuthCallback from "@/pages/AuthCallback";
import Business from "@/pages/Business";
import Admin from "@/pages/Admin";
import Refer from "@/pages/Refer";
import Onboarding from "@/pages/Onboarding";
import SkillCheck from "@/pages/SkillCheck";
import TopicsIndex from "@/pages/TopicsIndex";
import TopicDetail from "@/pages/TopicDetail";
import ChallengeDetail from "@/pages/ChallengeDetail";
import { Terms, Privacy, Refund, Contact } from "@/pages/Legal";
import ResetPassword from "@/pages/ResetPassword";
import BottomNav from "@/components/BottomNav";
import MobileAuditPanel from "@/components/MobileAuditPanel";
import InstallPrompt from "@/components/InstallPrompt";
import { Toaster } from "@/components/ui/sonner";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="spinner" />
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  // First-time users go through the 3-step quiz, except when they're already on it.
  // Skill Check claim auto-completes onboarding for "choose_for_me", so those
  // users skip /onboarding without ever seeing it.
  if (!user.onboarding_completed_at && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

/**
 * SkillCheckClaimer — when a user signs in and has a pending skill_check_id
 * stashed in localStorage (from before signup), drain it. The SkillCheck page
 * already handles this in its own flow, but this hook covers the edge case
 * where a user signs up from a different surface (Header, /pricing, etc.)
 * after having taken the skill check.
 *
 * Behavior: defaults to mode='build_my_own' so we never silently auto-assign
 * focuses without the user picking "Choose for Me" on the result screen.
 */
function SkillCheckClaimer() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (!user) return;
    if (location.pathname === "/skill-check") return; // SkillCheck handles its own claim
    let pending;
    try { pending = localStorage.getItem("pending_skill_check_id"); } catch { pending = null; }
    if (!pending) return;
    apiClient.post("/skill-check/claim", { skill_check_id: pending, mode: "build_my_own" })
      .then(() => {
        try { localStorage.removeItem("pending_skill_check_id"); } catch { /* ignore */ }
        capture("account_created_after_skill_check", { mode: "build_my_own", surface: "post_signup_drain" });
        refresh?.();
      })
      .catch(() => {
        // 404 (already-claimed/expired) — drop the stale id so we don't retry.
        try { localStorage.removeItem("pending_skill_check_id"); } catch { /* ignore */ }
      });
    // We deliberately re-run when the user object first appears, not on every render.
  }, [user?.user_id]);
  // Suppress unused-variable warning for `navigate` (kept for future redirect logic).
  void navigate;
  return null;
}

function AppRouter() {
  const location = useLocation();
  // Track one visit per session/day for the admin analytics tab.
  useVisitTracker();
  // Native visitor click/scroll/heartbeat tracker (Phase 1) — started
  // exactly once when the SPA boots. Idempotent; safe with React StrictMode.
  useEffect(() => {
    startVisitorTracker();
  }, []);
  // Synchronously detect OAuth callback fragment to avoid race with /auth/me
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <SkillCheckClaimer />
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/power-boosts" element={<PowerBoosts />} />
      <Route path="/business" element={<Business />} />
      <Route path="/topics" element={<TopicsIndex />} />
      <Route path="/topics/:slug" element={<TopicDetail />} />
      <Route path="/challenges/:slug" element={<ChallengeDetail />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/refund" element={<Refund />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/skill-check" element={<SkillCheck />} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/refer" element={<ProtectedRoute><Refer /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/lesson/:id" element={<ProtectedRoute><LessonPlayer /></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
      <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/coach" element={<ProtectedRoute><CoachPage /></ProtectedRoute>} />
      <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <CampaignProvider>
            <FestiveConfetti />
            <AmericaPromoTopBar />
            <AppRouter />
            <BottomNav />
            <InstallPrompt />
            <MobileAuditPanel />
            <Toaster position="top-center" richColors closeButton />
          </CampaignProvider>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
